import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { registerSchema, employerRegisterSchema } from '@hirebridge/validation'
import { ApiClientError } from '@hirebridge/api-client'
import { api, setActiveCompany } from '../lib/api'
import { useAuth } from '../lib/auth'
import { GoogleSignInButton } from '../components/GoogleSignInButton'

export function RegisterPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, loading, login, openLoginModal } = useAuth()

  // Initialize active tab from query param ?role=employer or ?role=candidate
  const initialRole = searchParams.get('role') === 'employer' ? 'employer' : 'candidate'
  const [activeTab, setActiveTab] = useState<'candidate' | 'employer'>(initialRole)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Auto-session check: redirect if user already logged in
  useEffect(() => {
    if (!loading && user) {
      if (user.companies && user.companies.length > 0) {
        navigate('/employer', { replace: true })
      } else if (user.roles && (user.roles.includes('employer') || user.roles.includes('owner') || user.roles.includes('admin') || user.roles.includes('recruiter'))) {
        navigate('/employer', { replace: true })
      } else {
        navigate('/candidate', { replace: true })
      }
    }
  }, [user, loading, navigate])

  // Password visibility states
  const [showCPassword, setShowCPassword] = useState(false)
  const [showCPasswordConfirm, setShowCPasswordConfirm] = useState(false)
  const [showEPassword, setShowEPassword] = useState(false)
  const [showEPasswordConfirm, setShowEPasswordConfirm] = useState(false)

  // Candidate state
  const [cFirstName, setCFirstName] = useState('')
  const [cLastName, setCLastName] = useState('')
  const [cEmail, setCEmail] = useState('')
  const [cPhone, setCPhone] = useState('')
  const [cPassword, setCPassword] = useState('')
  const [cPasswordConfirm, setCPasswordConfirm] = useState('')
  const [cTerms, setCTerms] = useState(false)

  // Employer state
  const [eFirstName, setEFirstName] = useState('')
  const [eLastName, setELastName] = useState('')
  const [eCompanyName, setECompanyName] = useState('')
  const [eEmail, setEEmail] = useState('')
  const [ePhone, setEPhone] = useState('')
  const [eWebsite, setEWebsite] = useState('')
  const [eSize, setESize] = useState<string>('11-50')
  const [ePassword, setEPassword] = useState('')
  const [ePasswordConfirm, setEPasswordConfirm] = useState('')
  const [eTerms, setETerms] = useState(false)

  // Sync state if URL query param changes
  useEffect(() => {
    const roleParam = searchParams.get('role')
    if (roleParam === 'employer' && activeTab !== 'employer') {
      setActiveTab('employer')
    } else if (roleParam === 'candidate' && activeTab !== 'candidate') {
      setActiveTab('candidate')
    }
  }, [searchParams])

  const handleTabSwitch = (tab: 'candidate' | 'employer') => {
    setActiveTab(tab)
    setError(null)
    setSearchParams({ role: tab })
  }

  const submitCandidate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const payload = {
      first_name: cFirstName,
      last_name: cLastName || undefined,
      email: cEmail,
      phone: cPhone || undefined,
      password: cPassword,
      password_confirmation: cPasswordConfirm,
      role: 'candidate' as const,
      terms_accepted: cTerms,
    }

    const validation = registerSchema.safeParse(payload)
    if (!validation.success) {
      setError(validation.error.issues[0]?.message || 'Please check the form fields.')
      return
    }

    setBusy(true)
    try {
      await api.register(payload)
      await login(cEmail, cPassword)
      navigate('/candidate')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Registration failed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const submitEmployer = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const payload = {
      first_name: eFirstName,
      last_name: eLastName || undefined,
      company_name: eCompanyName,
      email: eEmail,
      phone: ePhone,
      website: eWebsite || undefined,
      size: eSize as any,
      password: ePassword,
      password_confirmation: ePasswordConfirm,
      terms_accepted: eTerms,
    }

    const validation = employerRegisterSchema.safeParse(payload)
    if (!validation.success) {
      setError(validation.error.issues[0]?.message || 'Please check the form fields.')
      return
    }

    setBusy(true)
    try {
      // 1. Register user
      await api.register({
        first_name: eFirstName,
        last_name: eLastName || undefined,
        email: eEmail,
        phone: ePhone,
        password: ePassword,
        password_confirmation: ePasswordConfirm,
        role: 'employer',
        terms_accepted: true,
      })

      // 2. Log in
      await login(eEmail, ePassword)

      // 3. Create initial Company entity
      const companyRes = await api.createCompany({
        name: eCompanyName,
        website: eWebsite || undefined,
        size: eSize,
      })

      if (companyRes?.data?.id) {
        setActiveCompany(companyRes.data.id)
      }

      navigate('/employer')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Employer signup failed. Please check your details.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="register-page-wrapper">
      <div className="register-hero-header">
        <div className="brand-icon register-brand-icon" style={{ padding: '6px', background: 'transparent' }}>
          <img src="/logo-icon.png" alt="FibonacciPi" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <h1 className="register-title">Join FibonacciPi Today</h1>
        <p className="register-subtitle">
          Connect with top-tier technology opportunities or build your high-impact engineering team
        </p>

        {/* Dual Interactive Role Switcher Tabs */}
        <div className="register-tabs-container">
          <div className="register-tabs-track" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'candidate'}
              className={`register-tab ${activeTab === 'candidate' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('candidate')}
            >
              <span className="tab-icon">🎯</span>
              <div className="tab-text-group">
                <span className="tab-title">Job Seeker</span>
                <span className="tab-desc">Browse & apply to top verified roles</span>
              </div>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'employer'}
              className={`register-tab ${activeTab === 'employer' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('employer')}
            >
              <span className="tab-icon">💼</span>
              <div className="tab-text-group">
                <span className="tab-title">Hiring Team / Employer</span>
                <span className="tab-desc">Post jobs & screen top candidates</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="register-layout-grid">
        {/* Left / Main Form Card */}
        <div className="register-form-card">
          <div className="form-card-header">
            <h2>
              {activeTab === 'candidate'
                ? 'Create Your Candidate Profile'
                : 'Register Your Company & Hiring Account'}
            </h2>
            <p>
              {activeTab === 'candidate'
                ? 'Fill in your details to start applying to 15,000+ active tech jobs.'
                : 'Set up your company workspace to post jobs and manage candidate pipelines.'}
            </p>
          </div>

          {error && (
            <div className="alert-box alert-error register-alert" role="alert">
              <span>⚠️</span> {error}
            </div>
          )}

          {activeTab === 'candidate' ? (
            <>
              {/* Google OAuth 1-Click Sign Up */}
              <GoogleSignInButton
                role="candidate"
                mode="signup"
                onSuccess={() => navigate('/candidate')}
                onError={(err) => setError(err)}
              />

              <div className="auth-divider">
                <span>or sign up with email</span>
              </div>

              <form onSubmit={submitCandidate} className="auth-form" noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cFirst">First Name *</label>
                  <div className="input-with-icon">
                    <span className="input-icon">👤</span>
                    <input
                      id="cFirst"
                      type="text"
                      placeholder="e.g. Priya"
                      value={cFirstName}
                      onChange={(e) => setCFirstName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="cLast">Last Name</label>
                  <div className="input-with-icon">
                    <span className="input-icon">👤</span>
                    <input
                      id="cLast"
                      type="text"
                      placeholder="e.g. Sharma"
                      value={cLastName}
                      onChange={(e) => setCLastName(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="cEmail">Email Address *</label>
                <div className="input-with-icon">
                  <span className="input-icon">✉️</span>
                  <input
                    id="cEmail"
                    type="email"
                    placeholder="priya@example.com"
                    value={cEmail}
                    onChange={(e) => setCEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="cPhone">Mobile Phone (Optional for OTP alerts)</label>
                <div className="input-with-icon">
                  <span className="input-icon">📞</span>
                  <input
                    id="cPhone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={cPhone}
                    onChange={(e) => setCPhone(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cPass">Password *</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      id="cPass"
                      type={showCPassword ? 'text' : 'password'}
                      placeholder="Min 10 chars, letter + number"
                      value={cPassword}
                      onChange={(e) => setCPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowCPassword(!showCPassword)}
                      title={showCPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showCPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="cPassConfirm">Confirm Password *</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      id="cPassConfirm"
                      type={showCPasswordConfirm ? 'text' : 'password'}
                      placeholder="Repeat password"
                      value={cPasswordConfirm}
                      onChange={(e) => setCPasswordConfirm(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowCPasswordConfirm(!showCPasswordConfirm)}
                      title={showCPasswordConfirm ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showCPasswordConfirm ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              </div>

              <label className="checkbox-label terms-check">
                <input
                  type="checkbox"
                  checked={cTerms}
                  onChange={(e) => setCTerms(e.target.checked)}
                  required
                />
                <span>
                  I agree to the <Link to="/terms">Terms of Service</Link> & <Link to="/privacy">Privacy Policy</Link>
                </span>
              </label>

              <button type="submit" className="btn-auth-submit" disabled={busy}>
                {busy ? (
                  <span className="btn-busy-content">
                    <span className="spinner-inline"></span> Creating Candidate Account…
                  </span>
                ) : (
                  'Create Free Candidate Account'
                )}
              </button>
            </form>
            </>
          ) : (
            <>
              {/* Google OAuth 1-Click Sign Up for Employer */}
              <GoogleSignInButton
                role="employer"
                mode="signup"
                companyName={eCompanyName || undefined}
                onSuccess={() => navigate('/employer')}
                onError={(err) => setError(err)}
              />

              <div className="auth-divider">
                <span>or register company with email</span>
              </div>

              <form onSubmit={submitEmployer} className="auth-form" noValidate>
              <div className="form-group">
                <label htmlFor="eComp">Company / Organization Name *</label>
                <div className="input-with-icon">
                  <span className="input-icon">🏢</span>
                  <input
                    id="eComp"
                    type="text"
                    placeholder="e.g. Acme Innovations Pvt Ltd"
                    value={eCompanyName}
                    onChange={(e) => setECompanyName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eFirst">Contact Person First Name *</label>
                  <div className="input-with-icon">
                    <span className="input-icon">👤</span>
                    <input
                      id="eFirst"
                      type="text"
                      placeholder="e.g. Ramesh"
                      value={eFirstName}
                      onChange={(e) => setEFirstName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="eLast">Last Name</label>
                  <div className="input-with-icon">
                    <span className="input-icon">👤</span>
                    <input
                      id="eLast"
                      type="text"
                      placeholder="e.g. Gupta"
                      value={eLastName}
                      onChange={(e) => setELastName(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eEmail">Official Work Email *</label>
                  <div className="input-with-icon">
                    <span className="input-icon">✉️</span>
                    <input
                      id="eEmail"
                      type="email"
                      placeholder="ramesh@acme.com"
                      value={eEmail}
                      onChange={(e) => setEEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="ePhone">Work Phone *</label>
                  <div className="input-with-icon">
                    <span className="input-icon">📞</span>
                    <input
                      id="ePhone"
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={ePhone}
                      onChange={(e) => setEPhone(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eWeb">Company Website</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🌐</span>
                    <input
                      id="eWeb"
                      type="url"
                      placeholder="https://acme.com"
                      value={eWebsite}
                      onChange={(e) => setEWebsite(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="eSize">Company Size</label>
                  <select
                    id="eSize"
                    value={eSize}
                    onChange={(e) => setESize(e.target.value)}
                  >
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ePass">Account Password *</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      id="ePass"
                      type={showEPassword ? 'text' : 'password'}
                      placeholder="Min 10 chars"
                      value={ePassword}
                      onChange={(e) => setEPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowEPassword(!showEPassword)}
                      title={showEPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showEPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="ePassConfirm">Confirm Password *</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      id="ePassConfirm"
                      type={showEPasswordConfirm ? 'text' : 'password'}
                      placeholder="Repeat password"
                      value={ePasswordConfirm}
                      onChange={(e) => setEPasswordConfirm(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowEPasswordConfirm(!showEPasswordConfirm)}
                      title={showEPasswordConfirm ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showEPasswordConfirm ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              </div>

              <label className="checkbox-label terms-check">
                <input
                  type="checkbox"
                  checked={eTerms}
                  onChange={(e) => setETerms(e.target.checked)}
                  required
                />
                <span>
                  I agree to the <Link to="/terms">Terms of Service</Link> & Employer Verification Standards
                </span>
              </label>

              <button type="submit" className="btn-auth-submit btn-emerald-submit" disabled={busy}>
                {busy ? (
                  <span className="btn-busy-content">
                    <span className="spinner-inline"></span> Setting up Employer Account…
                  </span>
                ) : (
                  'Register Employer & Start Hiring'
                )}
              </button>
            </form>
            </>
          )}

          <div className="register-card-footer">
            Already have an account?{' '}
            <button
              type="button"
              className="link-button-inline"
              onClick={() => openLoginModal()}
            >
              Sign In to Account
            </button>
          </div>
        </div>

        {/* Right / Perks Card */}
        <div className="register-perks-card">
          {activeTab === 'candidate' ? (
            <>
              <div className="perks-badge">🎯 Job Seeker Privileges</div>
              <h3>Why Top Engineers Choose FibonacciPi</h3>
              <ul className="perks-list">
                <li>
                  <div className="perk-icon">⚡</div>
                  <div>
                    <strong>Direct Recruiter Reach</strong>
                    <p>Your profile is surfaced directly to hiring managers with verified technical matching.</p>
                  </div>
                </li>
                <li>
                  <div className="perk-icon">📄</div>
                  <div>
                    <strong>ATS-Compliant CV Builder</strong>
                    <p>Build and export AI-friendly resumes tested against leading enterprise parsers.</p>
                  </div>
                </li>
                <li>
                  <div className="perk-icon">💰</div>
                  <div>
                    <strong>Transparent Salary Data</strong>
                    <p>Real verified CTC ranges, stock options, and retention bonuses upfront.</p>
                  </div>
                </li>
                <li>
                  <div className="perk-icon">🔒</div>
                  <div>
                    <strong>Zero Spam & Privacy Controls</strong>
                    <p>Control company-specific blocking and anonymous profile browsing modes.</p>
                  </div>
                </li>
              </ul>

              <div className="perks-stat-banner">
                <div className="perk-stat-item">
                  <div className="stat-val">15k+</div>
                  <div className="stat-txt">Verified Jobs</div>
                </div>
                <div className="perk-stat-item">
                  <div className="stat-val">94%</div>
                  <div className="stat-txt">Response Rate</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="perks-badge perks-badge-employer">💼 Employer Suite</div>
              <h3>Scale Your Technical Hiring Pipeline</h3>
              <ul className="perks-list">
                <li>
                  <div className="perk-icon">🚀</div>
                  <div>
                    <strong>Instant Job Publishing</strong>
                    <p>Reach over 250,000+ active technology professionals in Bengaluru, Mumbai, NCR & Remote.</p>
                  </div>
                </li>
                <li>
                  <div className="perk-icon">🎯</div>
                  <div>
                    <strong>Knockout Pre-Screening</strong>
                    <p>Filter candidates automatically by experience, notice period, and specific stack skills.</p>
                  </div>
                </li>
                <li>
                  <div className="perk-icon">📋</div>
                  <div>
                    <strong>9-Stage Kanban Pipeline</strong>
                    <p>Track candidates from application to technical round, interview, and compliant offer.</p>
                  </div>
                </li>
                <li>
                  <div className="perk-icon">🤝</div>
                  <div>
                    <strong>Collaborative Team Hiring</strong>
                    <p>Invite interviewers, share candidate feedback notes, and track team hiring metrics.</p>
                  </div>
                </li>
              </ul>

              <div className="perks-stat-banner">
                <div className="perk-stat-item">
                  <div className="stat-val">3,200+</div>
                  <div className="stat-txt">Hiring Teams</div>
                </div>
                <div className="perk-stat-item">
                  <div className="stat-val">4.2x</div>
                  <div className="stat-txt">Faster Closure</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
